import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  Button,
  Container,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TemplatesList from './TemplatesList';
import ChannelsList from './ChannelsList';
import GroupsList from './GroupsList';
import CampaignsList from './CampaignsList';
import NotificationsList from './NotificationsList';
import SendNotification from './SendNotification';
import TopicsList from './TopicsList';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notifications-tabpanel-${index}`}
      aria-labelledby={`notifications-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `notifications-tab-${index}`,
    'aria-controls': `notifications-tabpanel-${index}`,
  };
}

const NotificationsMain: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get the tab index from URL hash or query param
  const getInitialTabValue = () => {
    const hash = location.hash.replace('#', '');
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    
    if (hash) {
      switch (hash) {
        case 'templates': return 0;
        case 'channels': return 1;
        case 'groups': return 2;
        case 'topics': return 3;
        case 'campaigns': return 4;
        case 'history': return 5;
        default: return 0;
      }
    } else if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 5) {
        return tabIndex;
      }
    }
    return 0;
  };
  
  const [value, setValue] = useState(getInitialTabValue());
  const [openSendDialog, setOpenSendDialog] = useState(false);

  // Update URL when tab changes
  useEffect(() => {
    const tabNames = ['templates', 'channels', 'groups', 'topics', 'campaigns', 'history'];
    navigate(`/connect#${tabNames[value]}`, { replace: true });
  }, [value, navigate]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleAddNew = () => {
    switch (value) {
      case 0: // Templates
        navigate('/connect/templates/new');
        break;
      case 1: // Channels
        navigate('/connect/channels/new');
        break;
      case 2: // Groups
        navigate('/connect/groups/new');
        break;
      case 3: // Topics
        navigate('/connect/topics/new');
        break;
      case 4: // Campaigns
        navigate('/connect/campaigns/new');
        break;
      case 5: // Direct Send
        setOpenSendDialog(true);
        break;
      default:
        break;
    }
  };

  const getButtonLabel = () => {
    switch (value) {
      case 0:
        return 'Add Template';
      case 1:
        return 'Add Channel';
      case 2:
        return 'Add Group';
      case 3:
        return 'Add Topic';
      case 4:
        return 'Create Campaign';
      case 5:
        return 'Send Now';
      default:
        return 'Add New';
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 28, mr: 1 }} />
              <Typography variant="h4" component="h1">
                Notification Management
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
            >
              {getButtonLabel()}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="notification management tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Templates" {...a11yProps(0)} />
            <Tab label="Channels" {...a11yProps(1)} />
            <Tab label="Groups" {...a11yProps(2)} />
            <Tab label="Topics" {...a11yProps(3)} />
            <Tab label="Campaigns" {...a11yProps(4)} />
            <Tab label="History" {...a11yProps(5)} />
          </Tabs>
        </Box>
        
        <TabPanel value={value} index={0}>
          <TemplatesList />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <ChannelsList />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <GroupsList />
        </TabPanel>
        <TabPanel value={value} index={3}>
          <TopicsList />
        </TabPanel>
        <TabPanel value={value} index={4}>
          <CampaignsList />
        </TabPanel>
        <TabPanel value={value} index={5}>
          <NotificationsList />
        </TabPanel>
      </Paper>

      <SendNotification
        open={openSendDialog}
        onClose={() => setOpenSendDialog(false)}
      />
    </Container>
  );
};

export default NotificationsMain;
